import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { shouldReportBoardError } from "../boardFailures";
import { type UpdateBoardAccessRequest } from "../BoardRecord";

import {
  cacheBoardWriteResponse,
  refreshBoardAndInvalidateLists,
} from "./refreshBoardQueries";

/**
 * Share a board with its organization, caching the record the server returns
 * (`cacheBoardWriteResponse`) and resolving with it. Whether or not the request
 * succeeded, the board's record is then refetched and the org's board lists
 * invalidated.
 */
export function useUpdateBoardAccess() {
  const { boardService } = useDomainServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      boardId,
      orgId,
      updates,
    }: {
      boardId: string;
      orgId: string;
      updates: UpdateBoardAccessRequest;
    }) => {
      try {
        const record = await boardService.updateBoardAccess(
          boardId,
          updates,
          orgId,
        );
        cacheBoardWriteResponse(queryClient, record);
        return record;
      } finally {
        await refreshBoardAndInvalidateLists(
          queryClient,
          boardService,
          boardId,
          orgId,
        );
      }
    },
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
